import { AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatAdaptive } from "@/utils/number";
import { FC } from "react";
import { DateTime } from "luxon";

interface DetailsProps {
	className?: string;
	breakdown: Dryp.IAndISummaryParts;
	// Only used to show the rainy day for the "lastRainyDay5mm" period.
	period?: Dryp.IAndISummary["period"];
}

// The backend sends the rainy day as 00:00 UTC, so read it in UTC to keep the same date.
const formatRainyDay = (rainyDay: string) => DateTime.fromISO(rainyDay, { zone: "utc" }).toFormat("dd LLL yyyy");

export const Details: FC<DetailsProps> = ({ breakdown, period, className }) => {
	const inflowFlowUnit = breakdown.inflow.flow?.unit.symbol;
	const inflowRatioUnit = breakdown.inflow.ratio?.unit.symbol;
	const wasteFlowUnit = breakdown.waste.flow?.unit.symbol;
	const wasteRatioUnit = breakdown.waste.ratio?.unit.symbol;
	const infiltrationFlowUnit = breakdown.infiltration.flow?.unit.symbol;
	const infiltrationRatioUnit = breakdown.infiltration.ratio?.unit.symbol;

	const segments = [
		{
			key: "Wastewater",
			value: breakdown.waste.flow?.value ?? 0,
			valueUnit: wasteFlowUnit,
			percent: breakdown.waste.ratio?.value ?? 0,
			percentUnit: wasteRatioUnit,
			color: "hsl(var(--chart-wastewater))",
		},
		{
			key: "Infiltration",
			value: breakdown.infiltration.flow?.value ?? 0,
			valueUnit: infiltrationFlowUnit,
			percent: breakdown.infiltration.ratio?.value ?? 0,
			percentUnit: infiltrationRatioUnit,
			color: "hsl(var(--chart-infiltration))",
		},
		{
			key: "Inflow",
			value: breakdown.inflow.flow?.value ?? 0,
			valueUnit: inflowFlowUnit,
			percent: breakdown.inflow.ratio?.value ?? 0,
			percentUnit: inflowRatioUnit,
			color: "hsl(var(--chart-rain))",
		},
	];

	const allZero = segments.every(s => (s.value ?? 0) === 0) && segments.every(s => (s.percent ?? 0) === 0);
	const hasNegative = segments.some(s => (s.value ?? 0) < 0 || (s.percent ?? 0) < 0);
	const negativeKeys = segments.filter(s => (s.value ?? 0) < 0 || (s.percent ?? 0) < 0).map(s => s.key);

	return (
		<div className={cn("space-y-2", className)}>
			<p className="mb-2 font-semibold flex items-center gap-2">I&I Breakdown</p>

			{!allZero && (
				<div className="space-y-2">
					{segments.map(({ key, value, valueUnit, percent, percentUnit, color }) => (
						<div key={key} className="flex items-center gap-1">
							<div
								className="h-3 w-3 min-w-3 min-h-3 rounded-full border border-white"
								style={{ backgroundColor: color }}
							/>
							<div className="min-w-20  truncate">{key}:</div>
							<div className=" w-full text-left tabular-nums flex text-nowrap">
								{formatAdaptive(value)}
								{` `}
								{valueUnit}
								<div className="w-3">
									{value < 0 ? <AlertTriangle className="h-3 w-3 ml-1" /> : null}
								</div>
							</div>
							<div className="w-12 text-right tabular-nums">
								({percent.toFixed(1)}
								{percentUnit})
							</div>
						</div>
					))}
				</div>
			)}

			{period?.rainyDay && (
				<div className="space-y-1 pt-1">
					<div className="flex gap-1">
						<div className="min-w-24">Last rainy day:</div>
						<div className="tabular-nums">{formatRainyDay(period.rainyDay)} (UTC)</div>
					</div>
					{period.rainDepth && (
						<div className="flex gap-1">
							<div className="min-w-24">Rain depth:</div>
							<div className="tabular-nums">
								{period.rainDepth.value.toFixed(3)} {period.rainDepth.unit.symbol}
							</div>
						</div>
					)}
				</div>
			)}

			{hasNegative && (
				<div className="mt-2 flex items-start gap-2 rounded-md text-[10px] leading-snug">
					<AlertTriangle className="mt-[2px] h-3 w-3 flex-none" />
					<div>
						<div className="font-medium">Negative values detected in time period</div>
						<div>
							{negativeKeys.join(", ")} {negativeKeys.length > 1 ? "have" : "has"} negative value. Review
							the configuration, mass balance and data quality before trusting this outcome.
						</div>
					</div>
				</div>
			)}
		</div>
	);
};
